// Task 201 D4 — project list page with tabbed edit panel.
//
// The SlidePanel is redesigned with four tabs:
//   Details      — name, client, slug, description, billable flag, meta
//   Taxonomy     — description (same as Details; editable), aliases, keywords
//   External refs — jiraProjectKey, githubRepos, slackChannels
//   Domain bindings — list + add / delete table
//
// Only the Details tab was wired before this task; the three new tabs are
// wired here for the first time.

import { useState, useMemo } from 'react';
import { useNavigate, useMatch, useParams } from 'react-router-dom';
import type { Project } from '@/types/entities';
import { PageHeader } from '@/components/composites/PageHeader';
import { DataTable } from '@/components/composites/DataTable';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { ActionLink } from '@/components/primitives/ActionLink';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Select } from '@/components/primitives/Select';
import { TextInput } from '@/components/primitives/TextInput';
import { Textarea } from '@/components/primitives/Textarea';
import { ChipInput } from '@/components/primitives/ChipInput';
import { useClients } from '@/hooks/useClients';
import { useProjects, useUpdateProject } from '@/hooks/useProjects';
import {
  useDomainBindings,
  useCreateDomainBinding,
  useDeleteDomainBinding,
} from '@/hooks/useDomainBindings';
import { formatDayFull } from '@/lib/dates';
import { noop } from '@/lib/noop';
import styles from './ManageProjectsPage.module.css';

// ── Cap constants ──────────────────────────────────────────────────────────

const PROJ_DESC_HARD = 500;
const PROJ_DESC_SOFT = 480;

// Syntactically-valid hostname regex mirroring the backend Zod validation.
// Leading '*.' (wildcard suffix) is allowed for suffix bindings.
const HOSTNAME_RE = /^(?:\*\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// ── Tab type ───────────────────────────────────────────────────────────────

type PanelTab = 'details' | 'taxonomy' | 'external-refs' | 'domain-bindings';

// ── ProjectEditPanel ───────────────────────────────────────────────────────

interface ProjectEditPanelProps {
  project: Project;
  clientOptions: { value: string; label: string }[];
  onClose: () => void;
}

function ProjectEditPanel({ project, clientOptions, onClose }: ProjectEditPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>('details');
  const [error, setError] = useState('');

  // Details tab state
  const [name, setName] = useState(project.name);
  const [clientId, setClientId] = useState(project.clientId ?? '');
  const [slug, setSlug] = useState(project.slug ?? project.name.toLowerCase().replace(/\s+/g, '-'));
  const [description, setDescription] = useState(project.description ?? '');
  const [billable, setBillable] = useState(project.type === 'billable');

  // Taxonomy tab state
  const [aliases, setAliases] = useState<string[]>(project.aliases ?? []);
  const [keywords, setKeywords] = useState<string[]>(project.keywords ?? []);

  // External refs tab state
  const extRefs = project.externalRefs ?? {};
  const [jiraKey, setJiraKey] = useState(extRefs.jiraProjectKey ?? '');
  const [githubRepos, setGithubRepos] = useState<string[]>(extRefs.githubRepos ?? []);
  const [slackChannels, setSlackChannels] = useState<string[]>(extRefs.slackChannels ?? []);

  // Domain bindings tab state
  const [newHost, setNewHost] = useState('');
  const [newKind, setNewKind] = useState<'exact' | 'suffix'>('exact');
  const [hostError, setHostError] = useState('');

  const descLen = description.length;
  const descOverSoft = descLen >= PROJ_DESC_SOFT;
  const descOverHard = descLen > PROJ_DESC_HARD;

  const updateProject = useUpdateProject();
  const { data: bindings, isLoading: bindingsLoading } = useDomainBindings(
    project.id,
    project.domainBindings,
  );
  const createBinding = useCreateDomainBinding(project.id);
  const deleteBinding = useDeleteDomainBinding(project.id);

  const isSaving = updateProject.isPending;

  function handleSave() {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (descOverHard) {
      setError(`Description must be ≤ ${PROJ_DESC_HARD} characters.`);
      return;
    }
    setError('');

    const trimmedDescription = description.trim();
    const jiraKeyTrimmed = jiraKey.trim();

    updateProject.mutate(
      {
        id: project.id,
        name: name.trim(),
        slug: slug.trim() || undefined,
        description: trimmedDescription === '' ? null : trimmedDescription,
        billable,
        aliases: aliases.length > 0 ? aliases : null,
        keywords: keywords.length > 0 ? keywords : null,
        externalRefs:
          jiraKeyTrimmed || githubRepos.length > 0 || slackChannels.length > 0
            ? {
                jiraProjectKey: jiraKeyTrimmed || undefined,
                githubRepos: githubRepos.length > 0 ? githubRepos : undefined,
                slackChannels: slackChannels.length > 0 ? slackChannels : undefined,
              }
            : null,
      },
      { onSuccess: onClose, onError: (err) => setError(err.message) },
    );
  }

  function handleAddBinding() {
    const host = newHost.trim().toLowerCase();
    if (!host) return;
    if (!HOSTNAME_RE.test(host)) {
      setHostError('Invalid hostname — must be a syntactically valid domain (e.g. example.com).');
      return;
    }
    setHostError('');
    createBinding.mutate(
      { host, kind: newKind, confidence: 1.0 },
      {
        onSuccess: () => {
          setNewHost('');
          setNewKind('exact');
        },
        onError: (err) => setHostError(err.message),
      },
    );
  }

  const tabs: Array<{ id: PanelTab; label: string }> = [
    { id: 'details', label: 'Details' },
    { id: 'taxonomy', label: 'Taxonomy' },
    { id: 'external-refs', label: 'External refs' },
    { id: 'domain-bindings', label: 'Domain bindings' },
  ];

  return (
    <SlidePanel
      open
      onClose={onClose}
      title="Edit Project"
      width="md"
      footer={
        <div className={styles.panelFooter}>
          {error && (
            <span className={styles.panelFooterError} role="alert">
              {error}
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      {/* Tab bar */}
      <div className={styles.tabBar} role="tablist" aria-label="Edit project sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            aria-selected={activeTab === t.id}
            aria-controls={`panel-${t.id}`}
            className={[styles.tabBtn, activeTab === t.id ? styles.tabBtnActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Details tab ─────────────────────────────────────────────────── */}
      <div
        id="panel-details"
        role="tabpanel"
        aria-labelledby="tab-details"
        hidden={activeTab !== 'details'}
        className={styles.tabPanel}
      >
        <TextInput label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Select
          label="Client"
          value={clientId}
          options={clientOptions}
          placeholder="No client (org-level)"
          onChange={setClientId}
        />
        <TextInput
          label="Slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          hint="Changing the slug may break existing integrations."
          className={styles.monoInput}
        />
        <div>
          <Textarea
            label="Description"
            placeholder="Optional. Short context that helps both reviewers and AI classification understand what this project is."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={PROJ_DESC_HARD}
            aria-describedby="proj-desc-counter"
          />
          <div
            id="proj-desc-counter"
            className={
              descOverHard
                ? styles.charCounterHard
                : descOverSoft
                  ? styles.charCounterSoft
                  : styles.charCounter
            }
            aria-live="polite"
          >
            {descLen} / {PROJ_DESC_HARD}
          </div>
        </div>
        <Checkbox
          checked={billable}
          onChange={setBillable}
          label="This project's hours are billable to the client"
        />
        <div className={styles.metaSection}>
          <div className={styles.metaRow}>
            <span className={styles.metaLabel}>Created</span>
            <span className={styles.metaValue}>{formatDayFull(project.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* ── Taxonomy tab ────────────────────────────────────────────────── */}
      <div
        id="panel-taxonomy"
        role="tabpanel"
        aria-labelledby="tab-taxonomy"
        hidden={activeTab !== 'taxonomy'}
        className={styles.tabPanel}
      >
        <p className={styles.tabDesc}>
          Taxonomy metadata is read by the auto-classifier Signal 1 to match events to this project.
          Keep names short and specific.
        </p>
        <ChipInput
          label="Aliases"
          values={aliases}
          onChange={setAliases}
          maxItems={20}
          softMaxItems={18}
          maxItemLength={32}
          placeholder="Type an alias and press Enter"
          hint="Alternative names for this project (≤ 20, each ≤ 32 chars)."
        />
        <ChipInput
          label="Keywords"
          values={keywords}
          onChange={setKeywords}
          maxItems={20}
          softMaxItems={18}
          maxItemLength={32}
          placeholder="Type a keyword and press Enter"
          hint="Signal 1 keywords matched against desktop activity (≤ 20, each ≤ 32 chars)."
        />
      </div>

      {/* ── External refs tab ───────────────────────────────────────────── */}
      <div
        id="panel-external-refs"
        role="tabpanel"
        aria-labelledby="tab-external-refs"
        hidden={activeTab !== 'external-refs'}
        className={styles.tabPanel}
      >
        <p className={styles.tabDesc}>
          Link this project to external systems. Values are used by the auto-classifier to match
          activity from Jira, GitHub, and Slack.
        </p>
        <TextInput
          label="Jira project key"
          value={jiraKey}
          onChange={(e) => setJiraKey(e.target.value.toUpperCase())}
          placeholder="e.g. KAN"
          hint="Uppercase letters and digits only (e.g. KAN, MYPROJECT)."
          className={styles.monoInput}
        />
        <ChipInput
          label="GitHub repos"
          values={githubRepos}
          onChange={setGithubRepos}
          maxItems={20}
          maxItemLength={80}
          placeholder="owner/repo"
          hint="In owner/repo format (≤ 20, each ≤ 80 chars)."
        />
        <ChipInput
          label="Slack channels"
          values={slackChannels}
          onChange={setSlackChannels}
          maxItems={20}
          maxItemLength={80}
          placeholder="#channel-name"
          hint="Leading # is optional (≤ 20, each ≤ 80 chars)."
        />
      </div>

      {/* ── Domain bindings tab ─────────────────────────────────────────── */}
      <div
        id="panel-domain-bindings"
        role="tabpanel"
        aria-labelledby="tab-domain-bindings"
        hidden={activeTab !== 'domain-bindings'}
        className={styles.tabPanel}
      >
        <p className={styles.tabDesc}>
          Browser events from these hosts are matched to this project by the auto-classifier. Use
          <strong> exact</strong> for a single hostname and <strong>suffix</strong> for a domain and
          all its subdomains (e.g. <code>*.example.com</code>).
        </p>

        {/* Add binding row */}
        <div className={styles.addBindingRow}>
          <TextInput
            label="Host"
            value={newHost}
            onChange={(e) => {
              setNewHost(e.target.value);
              if (hostError) setHostError('');
            }}
            placeholder="e.g. app.example.com or *.example.com"
            error={hostError || undefined}
            className={styles.hostInput}
          />
          <div className={styles.addBindingControls}>
            <Select
              label="Kind"
              value={newKind}
              options={[
                { value: 'exact', label: 'Exact' },
                { value: 'suffix', label: 'Suffix' },
              ]}
              onChange={(v) => setNewKind(v as 'exact' | 'suffix')}
            />
            <Button
              variant="secondary"
              onClick={handleAddBinding}
              disabled={createBinding.isPending || !newHost.trim()}
            >
              {createBinding.isPending ? 'Adding…' : 'Add binding'}
            </Button>
          </div>
        </div>

        {/* Binding list */}
        {bindingsLoading ? (
          <p className={styles.bindingsLoading}>Loading…</p>
        ) : bindings.length === 0 ? (
          <p className={styles.bindingsEmpty}>No domain bindings yet.</p>
        ) : (
          <table className={styles.bindingsTable} aria-label="Domain bindings">
            <thead>
              <tr>
                <th scope="col" className={styles.bindingsTh}>
                  Host
                </th>
                <th scope="col" className={styles.bindingsTh}>
                  Kind
                </th>
                <th scope="col" className={styles.bindingsTh}>
                  <span className={styles.srOnly}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {bindings.map((b) => (
                <tr key={b.id} className={styles.bindingsRow}>
                  <td className={`${styles.bindingsTd} ${styles.bindingHost}`}>{b.host}</td>
                  <td className={styles.bindingsTd}>
                    <Badge variant={b.kind === 'exact' ? 'billable' : 'general'}>{b.kind}</Badge>
                  </td>
                  <td className={`${styles.bindingsTd} ${styles.bindingsActionsCell}`}>
                    <ActionLink
                      danger
                      disabled={deleteBinding.isPending}
                      onClick={() => {
                        deleteBinding.mutate(b.id);
                      }}
                    >
                      Delete
                    </ActionLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </SlidePanel>
  );
}

// ── Row type ───────────────────────────────────────────────────────────────

interface ProjectRow {
  project: Project;
  clientName: string | null;
}

// ── Main page ──────────────────────────────────────────────────────────────

export function ManageProjectsPage() {
  const navigate = useNavigate();
  const editMatch = useMatch('/projects/:projectId/edit');
  const { projectId: editProjectId } = useParams<{ projectId: string }>();

  const { data: projects } = useProjects();
  const { data: clients } = useClients();

  const [clientFilter, setClientFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.name })),
    [clients],
  );

  const typeOptions = [
    { value: 'billable', label: 'Billable' },
    { value: 'general', label: 'General' },
  ] as const;

  const rows: ProjectRow[] = useMemo(() => {
    return projects
      .map((p) => {
        const client = p.clientId ? (clients.find((c) => c.id === p.clientId) ?? null) : null;
        return { project: p, clientName: client?.name ?? null };
      })
      .filter((row) => {
        if (clientFilter && row.project.clientId !== clientFilter) return false;
        if (typeFilter && row.project.type !== typeFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          if (
            !row.project.name.toLowerCase().includes(q) &&
            !(row.clientName ?? '').toLowerCase().includes(q)
          )
            return false;
        }
        return true;
      });
  }, [projects, clients, clientFilter, typeFilter, search]);

  const editingProject = editProjectId ? projects.find((p) => p.id === editProjectId) : undefined;
  // Guard the edit panel for system-owned projects even if the user navigates
  // directly to the URL — the backend returns 409 SYSTEM_OWNED on PATCH, so
  // rendering the form would be misleading. Disabled affordances in the table
  // already prevent normal navigation here.
  const editPanelBlocked = !!editingProject && editingProject.systemOwned;
  const panelOpen = !!editMatch && !editPanelBlocked;
  const closePanel = () => navigate('/projects');

  type Col = 'project' | 'client' | 'type' | 'slug' | 'created' | 'actions';

  const columns: Array<{
    key: Col;
    header: string;
    align?: 'left' | 'right';
    render: (row: ProjectRow) => React.ReactNode;
  }> = [
    {
      key: 'project',
      header: 'Project',
      render: (row) => <span className={styles.projectName}>{row.project.name}</span>,
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => (row.clientName ? row.clientName : <>&mdash;</>),
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => (
        <Badge variant={row.project.type}>
          {row.project.type[0].toUpperCase() + row.project.type.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      render: (row) => (
        <span className={styles.slugCell}>
          {row.project.name.toLowerCase().replace(/\s+/g, '-')}
        </span>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      render: (row) => (
        <span className={styles.createdCell}>{formatDayFull(row.project.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        const locked = row.project.systemOwned;
        const lockedTitle = 'System-owned — managed by Kanjoy';
        return (
          <div className={styles.actionsCell}>
            <ActionLink
              disabled={locked}
              title={locked ? lockedTitle : undefined}
              onClick={locked ? undefined : () => navigate(`/projects/${row.project.id}/edit`)}
            >
              Edit
            </ActionLink>
            <span className={styles.actionSep}>&middot;</span>
            <ActionLink
              danger
              disabled={locked}
              title={locked ? lockedTitle : undefined}
              onClick={locked ? undefined : noop}
            >
              Delete
            </ActionLink>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Projects" />

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <Select
          value={clientFilter}
          options={clientOptions}
          placeholder="All Clients"
          onChange={(v) => setClientFilter(v)}
          size="sm"
        />
        <Select
          value={typeFilter}
          options={typeOptions}
          placeholder="All"
          onChange={(v) => setTypeFilter(v)}
          size="sm"
        />
        <div className={styles.searchWrap}>
          <svg
            className={styles.searchIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className={styles.filterSpacer} />
        <span className={styles.filterCount}>{rows.length} projects</span>
      </div>

      <DataTable<ProjectRow, Col>
        caption="All projects"
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.project.id}
      />

      {panelOpen && editingProject && (
        <ProjectEditPanel
          project={editingProject}
          clientOptions={clientOptions}
          onClose={closePanel}
        />
      )}
    </>
  );
}
